export const REPEAT_SCHEDULES = ['hourly', 'daily', 'weekly', 'monthly', 'custom'] as const;
export type RepeatSchedule = (typeof REPEAT_SCHEDULES)[number];

export const SCHEDULE_META_KEYS = [
  'completed',
  'scheduleKey',
  'scheduledAt',
  'schedulePreset',
  'scheduleLabel',
  '_scheduled',
] as const;

export const VIRTUAL_SCHEDULE_PREFIX = 'virtual:';
export const VISIBLE_MISSED_SLOT_LIMIT = 2;
export const VISIBLE_UPCOMING_SLOT_LIMIT = 1;
export const REPEATABLE_SLOT_REMIND_MINUTES = 5;

export type ScheduleSlotTone = 'missed' | 'upcoming' | 'scheduled';

export type RepeatableDatasetSettings = {
  repeatable?: boolean;
  repeatSchedule?: RepeatSchedule;
  repeatCron?: string;
  repeatTimezone?: string;
  repeatTimes?: string[];
  notifyEmail?: boolean;
  notifyPush?: boolean;
  notifyReminders?: boolean;
};

export type DatasetNotificationPrefs = {
  notifyEmail: boolean;
  notifyPush: boolean;
  notifyReminders: boolean;
};

/** Missing keys mean enabled, so datasets saved before these settings keep notifying. */
export function readDatasetNotificationPrefs(settings?: RepeatableDatasetSettings | null): DatasetNotificationPrefs {
  return {
    notifyEmail: settings?.notifyEmail !== false,
    notifyPush: settings?.notifyPush !== false,
    notifyReminders: settings?.notifyReminders !== false,
  };
}

export type ScheduleSlot = {
  scheduleKey: string;
  scheduleLabel: string;
  scheduledAt: string;
  schedulePreset: string;
};

export type UpcomingSlotReminder = {
  scheduleKey: string;
  scheduleLabel: string;
  dueAt: Date;
  remindAt: Date;
};

export const DEFAULT_CUSTOM_CRON = '0 9 * * *';
export const DEFAULT_REPEAT_TIME = '09:00';
export const MAX_REPEAT_TIMES = 8;

const PRESET_CRON: Record<Exclude<RepeatSchedule, 'custom'>, string> = {
  hourly: '0 * * * *',
  daily: '0 9 * * *',
  weekly: '0 9 * * 1',
  monthly: '0 9 1 * *',
};

export function isScheduleMetaKey(key: string): boolean {
  return (SCHEDULE_META_KEYS as readonly string[]).includes(key);
}

export function resolveRepeatTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function buildRepeatSettingsPayload(input: {
  repeatable: boolean;
  repeatSchedule: RepeatSchedule;
  repeatCron?: string;
  repeatTimes?: string[];
}): RepeatableDatasetSettings {
  const payload: RepeatableDatasetSettings = {
    repeatable: input.repeatable,
    repeatTimezone: resolveRepeatTimezone(),
  };
  if (input.repeatable) {
    payload.repeatSchedule = input.repeatSchedule;
    if (input.repeatSchedule === 'custom') {
      payload.repeatCron = (input.repeatCron || DEFAULT_CUSTOM_CRON).trim();
      payload.repeatTimes = [];
    } else if (input.repeatSchedule === 'hourly') {
      payload.repeatCron = '';
      payload.repeatTimes = [];
    } else {
      payload.repeatCron = '';
      payload.repeatTimes = normalizeRepeatTimes(input.repeatTimes);
      if (!payload.repeatTimes.length) payload.repeatTimes = [DEFAULT_REPEAT_TIME];
    }
  }
  return payload;
}

export function parseRepeatSettingsFromDataset(dataset?: {
  completable?: boolean;
  settings?: RepeatableDatasetSettings | null;
}) {
  const settings = dataset?.settings && typeof dataset.settings === 'object' ? dataset.settings : {};
  const times = normalizeRepeatTimes(settings.repeatTimes);
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase() as RepeatSchedule;
  return {
    completable: Boolean(dataset?.completable) || Boolean(settings.repeatable),
    repeatable: Boolean(settings.repeatable),
    repeatSchedule: REPEAT_SCHEDULES.includes(preset) ? preset : 'daily',
    repeatCron: settings.repeatCron || DEFAULT_CUSTOM_CRON,
    repeatTimes: times.length ? times : [DEFAULT_REPEAT_TIME],
  };
}

export function normalizeRepeatTime(value?: string): string {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return '';
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour > 23 || minute > 59) return '';
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function normalizeRepeatTimes(times?: string[]): string[] {
  if (!Array.isArray(times)) return [];
  return [...new Set(times.map(normalizeRepeatTime).filter(Boolean))].sort().slice(0, MAX_REPEAT_TIMES);
}

export function resolveRepeatTimes(settings: RepeatableDatasetSettings = {}): string[] {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase();
  if (preset === 'hourly' || preset === 'custom') return [];
  const times = normalizeRepeatTimes(settings.repeatTimes);
  return times.length ? times : [DEFAULT_REPEAT_TIME];
}

export function nextRepeatTime(times: string[]): string {
  const normalized = normalizeRepeatTimes(times);
  if (!normalized.length) return DEFAULT_REPEAT_TIME;
  for (let hour = 0; hour < 24; hour += 1) {
    const last = normalized[normalized.length - 1];
    const [lastHour, lastMinute] = last.split(':').map(Number);
    const candidate = `${String((lastHour + 1 + hour) % 24).padStart(2, '0')}:${String(lastMinute).padStart(2, '0')}`;
    if (!normalized.includes(candidate)) return candidate;
  }
  return '';
}

export function getItemScheduleLabel(item: any): string {
  return String(item?.data?.scheduleLabel || item?.scheduleLabel || '').trim();
}

export function isScheduledDataItem(item: any): boolean {
  return Boolean(item?.data?._scheduled || item?.data?.scheduleKey);
}

export function isVirtualScheduleItem(item: any): boolean {
  return Boolean(item?._virtual) || String(item?._id || '').startsWith(VIRTUAL_SCHEDULE_PREFIX);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isValidTimeZone(tz: string): boolean {
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function zonedParts(date: Date, timeZone = 'UTC') {
  const tz = timeZone && isValidTimeZone(timeZone) ? timeZone : 'UTC';
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      hourCycle: 'h23',
    });
    const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
    const weekdayMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      weekday: weekdayMap[parts.weekday] ?? 0,
    };
  } catch {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      weekday: date.getUTCDay(),
    };
  }
}

function isoWeekKey(parts: { year: number; month: number; day: number }): string {
  const utc = Date.UTC(parts.year, parts.month - 1, parts.day);
  const date = new Date(utc);
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${pad(week)}`;
}

function cronFieldMatches(field: string, value: number): boolean {
  if (field == null || field === '*') return true;
  if (field.includes(',')) return field.split(',').some((part) => cronFieldMatches(part, value));
  if (field.includes('/')) {
    const [base, stepRaw] = field.split('/');
    const step = Number(stepRaw);
    if (!Number.isFinite(step) || step <= 0) return false;
    if (base === '*') return value % step === 0;
    const start = Number(base.split('-')[0]);
    return value >= start && (value - start) % step === 0;
  }
  if (field.includes('-')) {
    const [start, end] = field.split('-').map(Number);
    return value >= start && value <= end;
  }
  return Number(field) === value;
}

function validateCron(expr?: string): string | null {
  const raw = String(expr || '').trim();
  const parts = raw.split(/\s+/);
  if (parts.length !== 5) return null;
  return raw;
}

function resolveCron(settings: RepeatableDatasetSettings = {}): string {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase() as RepeatSchedule;
  if (preset === 'custom') return validateCron(settings.repeatCron) || PRESET_CRON.daily;
  return PRESET_CRON[preset] || PRESET_CRON.daily;
}

function customGranularity(cronExpression?: string): RepeatSchedule {
  const parsed = validateCron(cronExpression);
  if (!parsed) return 'daily';
  const [minute, hour, day, month, weekday] = parsed.split(/\s+/);
  const wild = (field: string) => field === '*';
  if (!wild(weekday) && wild(day)) return 'weekly';
  if (!wild(day) || !wild(month)) return 'monthly';
  if (!wild(hour)) return 'daily';
  if (!wild(minute)) return 'hourly';
  return 'daily';
}

function withRepeatTimeSuffix(base: string, settings: RepeatableDatasetSettings, parts: { hour: number; minute: number }): string {
  const times = normalizeRepeatTimes(settings.repeatTimes);
  if (times.length <= 1) return base;
  const nowMins = parts.hour * 60 + parts.minute;
  let reached = times[0];
  for (const time of times) {
    const [hour, minute] = time.split(':').map(Number);
    if (hour * 60 + minute <= nowMins) reached = time;
  }
  return `${base}T${reached}`;
}

export function getSchedulePeriodKey(settings: RepeatableDatasetSettings = {}, now = new Date()): string {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase();
  const tz = settings.repeatTimezone || 'UTC';
  const parts = zonedParts(now, tz);
  const dateKey = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  if (preset === 'hourly') return `hourly:${dateKey}T${pad(parts.hour)}`;
  if (preset === 'weekly') return withRepeatTimeSuffix(`weekly:${isoWeekKey(parts)}`, settings, parts);
  if (preset === 'monthly') return withRepeatTimeSuffix(`monthly:${parts.year}-${pad(parts.month)}`, settings, parts);
  if (preset === 'custom') {
    const gran = customGranularity(settings.repeatCron);
    if (gran === 'hourly') return `custom:${dateKey}T${pad(parts.hour)}`;
    if (gran === 'weekly') return `custom:${isoWeekKey(parts)}`;
    if (gran === 'monthly') return `custom:${parts.year}-${pad(parts.month)}`;
    return `custom:${dateKey}`;
  }
  return withRepeatTimeSuffix(`daily:${dateKey}`, settings, parts);
}

function isTimeReached(parts: { hour: number; minute: number }, hourF: string, minuteF: string): boolean {
  const hour = hourF === '*' ? 0 : Number(String(hourF).split(',')[0].split('-')[0].split('/')[0]) || 0;
  const minute = minuteF === '*' ? 0 : Number(String(minuteF).split(',')[0].split('-')[0].split('/')[0]) || 0;
  if (parts.hour > hour) return true;
  if (parts.hour < hour) return false;
  return parts.minute >= minute;
}

export function isScheduleDue(settings: RepeatableDatasetSettings = {}, now = new Date()): boolean {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase();
  const tz = settings.repeatTimezone || 'UTC';
  const parts = zonedParts(now, tz);
  const cron = resolveCron(settings);
  const [minuteF, hourF, dayF, , weekdayF] = cron.split(/\s+/);
  const gran = preset === 'custom' ? customGranularity(settings.repeatCron) : preset;
  const times = normalizeRepeatTimes(settings.repeatTimes);
  const timeReached = times.length
    ? times.some((time) => {
      const [hour, minute] = time.split(':').map(Number);
      return isTimeReached(parts, String(hour), String(minute));
    })
    : isTimeReached(parts, hourF, minuteF);

  if (gran === 'hourly') {
    const minute = minuteF === '*' ? 0 : Number(String(minuteF).split(',')[0].split('-')[0].split('/')[0]) || 0;
    return parts.minute >= minute;
  }
  if (gran === 'weekly') {
    const weekday = weekdayF === '*' ? 1 : Number(String(weekdayF).split(',')[0]);
    if (parts.weekday < weekday) return false;
    if (parts.weekday > weekday) return true;
    return timeReached;
  }
  if (gran === 'monthly') {
    const day = dayF === '*' ? 1 : Number(String(dayF).split(',')[0]);
    if (parts.day < day) return false;
    if (parts.day > day) return true;
    return timeReached;
  }
  return timeReached;
}

export function formatScheduleLabel(settings: RepeatableDatasetSettings = {}, now = new Date()): string {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase();
  const tz = settings.repeatTimezone || 'UTC';
  const parts = zonedParts(now, tz);
  const dateLabel = `${parts.day} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parts.month - 1] || ''} ${parts.year}`;
  const times = normalizeRepeatTimes(settings.repeatTimes);
  const timeSuffix = times.length > 1 ? ` ${withRepeatTimeSuffix('', settings, parts).slice(1)}` : '';
  if (preset === 'hourly') return `${dateLabel} ${pad(parts.hour)}:00`;
  if (preset === 'weekly') return `Week ${isoWeekKey(parts)}${timeSuffix}`;
  if (preset === 'monthly') return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][parts.month - 1] || ''} ${parts.year}${timeSuffix}`;
  return `${dateLabel}${timeSuffix}`;
}

function slotStepMs(settings: RepeatableDatasetSettings = {}): number {
  if (normalizeRepeatTimes(settings.repeatTimes).length > 1) return 60 * 60 * 1000;
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase();
  const gran = preset === 'custom' ? customGranularity(settings.repeatCron) : preset;
  return gran === 'hourly' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
}

function slotLookbackMs(settings: RepeatableDatasetSettings = {}): number {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase();
  const gran = preset === 'custom' ? customGranularity(settings.repeatCron) : preset;
  if (gran === 'hourly') return 48 * 60 * 60 * 1000;
  if (gran === 'weekly') return 12 * 7 * 24 * 60 * 60 * 1000;
  if (gran === 'monthly') return 365 * 24 * 60 * 60 * 1000;
  return 30 * 24 * 60 * 60 * 1000;
}

function scheduleGranularity(settings: RepeatableDatasetSettings = {}): RepeatSchedule {
  const preset = String(settings.repeatSchedule || 'daily').toLowerCase() as RepeatSchedule;
  return preset === 'custom' ? customGranularity(settings.repeatCron) : (PRESET_CRON[preset] ? preset : 'daily');
}

function cronClock(settings: RepeatableDatasetSettings = {}) {
  const cron = resolveCron(settings);
  const [minuteF, hourF, dayF, , weekdayF] = cron.split(/\s+/);
  const firstNum = (field: string, fallback: number) => {
    if (field == null || field === '*') return fallback;
    const parsed = Number(String(field).split(',')[0].split('-')[0].split('/')[0]);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  return {
    minute: firstNum(minuteF, 0),
    hour: firstNum(hourF, 9),
    day: firstNum(dayF, 1),
    weekday: firstNum(weekdayF, 1),
  };
}

function zonedDateFromParts(
  parts: { year: number; month: number; day: number; hour: number; minute: number },
  timeZone: string,
): Date {
  let utc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0);
  for (let i = 0; i < 4; i++) {
    const got = zonedParts(new Date(utc), timeZone);
    const gotMs = Date.UTC(got.year, got.month - 1, got.day, got.hour, got.minute);
    const wantMs = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
    const delta = wantMs - gotMs;
    if (delta === 0) break;
    utc += delta;
  }
  return new Date(utc);
}

function shiftZonedDate(parts: { year: number; month: number; day: number; hour: number; minute: number }, days: number) {
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days, 12, 0, 0));
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: parts.hour,
    minute: parts.minute,
  };
}

export function getSlotDueAt(settings: RepeatableDatasetSettings = {}, periodDate = new Date()): Date {
  const tz = settings.repeatTimezone || 'UTC';
  const parts = zonedParts(periodDate, tz);
  const cron = cronClock(settings);
  const times = normalizeRepeatTimes(settings.repeatTimes);
  let clock = cron;
  if (times.length) {
    const nowMins = parts.hour * 60 + parts.minute;
    let time = times[0];
    for (const candidate of times) {
      const [hour, minute] = candidate.split(':').map(Number);
      if (hour * 60 + minute <= nowMins) time = candidate;
    }
    const [hour, minute] = time.split(':').map(Number);
    clock = { ...cron, hour, minute };
  }
  const gran = scheduleGranularity(settings);

  if (gran === 'hourly') {
    return zonedDateFromParts({ ...parts, minute: clock.minute }, tz);
  }
  if (gran === 'weekly') {
    const dueDay = shiftZonedDate(parts, clock.weekday - parts.weekday);
    return zonedDateFromParts({ ...dueDay, hour: clock.hour, minute: clock.minute }, tz);
  }
  if (gran === 'monthly') {
    return zonedDateFromParts({ ...parts, day: clock.day, hour: clock.hour, minute: clock.minute }, tz);
  }
  return zonedDateFromParts({ ...parts, hour: clock.hour, minute: clock.minute }, tz);
}

function reminderHorizonMs(settings: RepeatableDatasetSettings = {}, limit: number): number {
  const gran = scheduleGranularity(settings);
  if (gran === 'hourly') return (limit + 4) * 60 * 60 * 1000;
  if (gran === 'weekly') return (limit + 1) * 7 * 24 * 60 * 60 * 1000;
  if (gran === 'monthly') return (limit + 1) * 32 * 24 * 60 * 60 * 1000;
  return (limit + 2) * 24 * 60 * 60 * 1000;
}

export function reminderLimitForSchedule(settings: RepeatableDatasetSettings = {}): number {
  const times = normalizeRepeatTimes(settings.repeatTimes).length;
  if (times > 1) return Math.max(times, 2);
  return scheduleGranularity(settings) === 'hourly' ? 3 : 2;
}

export function listUpcomingSlotReminders(
  settings: RepeatableDatasetSettings = {},
  {
    now = new Date(),
    filledKeys = [],
    remindMinutesBefore = REPEATABLE_SLOT_REMIND_MINUTES,
    limit = reminderLimitForSchedule(settings),
  }: {
    now?: Date;
    filledKeys?: string[];
    remindMinutesBefore?: number;
    limit?: number;
  } = {},
): UpcomingSlotReminder[] {
  const filled = new Set((filledKeys || []).filter(Boolean).map(String));
  const remindMs = Math.max(0, remindMinutesBefore) * 60 * 1000;
  const step = slotStepMs(settings);
  const horizon = now.getTime() + reminderHorizonMs(settings, limit);
  const seen = new Set<string>();
  const results: UpcomingSlotReminder[] = [];

  for (
    let cursor = new Date(now.getTime() - step);
    cursor.getTime() <= horizon && results.length < limit;
    cursor = new Date(cursor.getTime() + step)
  ) {
    const scheduleKey = getSchedulePeriodKey(settings, cursor);
    if (seen.has(scheduleKey) || filled.has(scheduleKey)) continue;
    seen.add(scheduleKey);
    const dueAt = getSlotDueAt(settings, cursor);
    const remindAt = new Date(dueAt.getTime() - remindMs);
    if (remindAt.getTime() <= now.getTime()) continue;
    results.push({
      scheduleKey,
      scheduleLabel: formatScheduleLabel(settings, cursor),
      dueAt,
      remindAt,
    });
  }
  return results;
}

export function getNextScheduleDueAt(
  settings: RepeatableDatasetSettings = {},
  now = new Date(),
): UpcomingSlotReminder | null {
  return listUpcomingSlotReminders(settings, { now, remindMinutesBefore: 0, limit: 1 })[0] || null;
}

export function enumerateScheduleSlots(
  settings: RepeatableDatasetSettings = {},
  { from, to, limit = 80 }: { from?: Date | string; to?: Date; limit?: number } = {},
): ScheduleSlot[] {
  const end = to instanceof Date ? to : new Date();
  const lookbackStart = new Date(end.getTime() - slotLookbackMs(settings));
  const explicitStart = from ? new Date(from) : lookbackStart;
  const start = Number.isNaN(explicitStart.getTime())
    ? lookbackStart
    : new Date(Math.max(explicitStart.getTime(), lookbackStart.getTime()));
  const step = slotStepMs(settings);
  const slots: ScheduleSlot[] = [];
  const seen = new Set<string>();
  for (let cursor = new Date(start); cursor.getTime() <= end.getTime() && slots.length < limit; cursor = new Date(cursor.getTime() + step)) {
    const scheduleKey = getSchedulePeriodKey(settings, cursor);
    if (seen.has(scheduleKey)) continue;
    seen.add(scheduleKey);
    slots.push({
      scheduleKey,
      scheduleLabel: formatScheduleLabel(settings, cursor),
      scheduledAt: cursor.toISOString(),
      schedulePreset: String(settings.repeatSchedule || 'daily'),
    });
  }
  return slots;
}

function reminderToSlot(settings: RepeatableDatasetSettings, reminder: UpcomingSlotReminder): ScheduleSlot {
  return {
    scheduleKey: reminder.scheduleKey,
    scheduleLabel: reminder.scheduleLabel,
    scheduledAt: reminder.dueAt.toISOString(),
    schedulePreset: String(settings.repeatSchedule || 'daily'),
  };
}

export function classifyScheduleSlots(
  settings: RepeatableDatasetSettings = {},
  filledKeys: string[] = [],
  {
    from,
    now = new Date(),
    upcomingLimit = VISIBLE_UPCOMING_SLOT_LIMIT,
  }: { from?: Date | string; now?: Date; upcomingLimit?: number } = {},
) {
  const filled = new Set((filledKeys || []).filter(Boolean).map(String));
  const periodKey = getSchedulePeriodKey(settings, now);
  const currentKey = isScheduleDue(settings, now) ? periodKey : null;
  const slots = enumerateScheduleSlots(settings, { from, to: now });
  const missed: ScheduleSlot[] = [];
  let current: ScheduleSlot | null = null;
  for (const slot of slots) {
    if (filled.has(slot.scheduleKey)) continue;
    if (currentKey && slot.scheduleKey === currentKey) {
      current = slot;
      continue;
    }
    // Today's (or this hour's) slot is enumerated as soon as the period starts,
    // but it is not missed until the scheduled clock time has actually passed.
    if (slot.scheduleKey === periodKey) continue;
    const dueAt = getSlotDueAt(settings, new Date(slot.scheduledAt));
    if (dueAt.getTime() > now.getTime()) continue;
    missed.push(slot);
  }
  const upcoming = listUpcomingSlotReminders(settings, {
    now,
    filledKeys: [...filled],
    remindMinutesBefore: 0,
    limit: Math.max(1, upcomingLimit) + (currentKey ? 1 : 0),
  })
    .filter((slot) => slot.scheduleKey !== currentKey)
    .slice(0, Math.max(0, upcomingLimit))
    .map((slot) => reminderToSlot(settings, slot));
  return { current, missed, upcoming, currentKey };
}

export function emptyItemDataFromSchema(schema: any, extra: Record<string, any> = {}) {
  const data: Record<string, any> = { ...extra };
  if (!Array.isArray(schema)) return data;
  for (const field of schema) {
    if (!field || typeof field !== 'object') continue;
    let title = field?.title || field?.key || field?.name;
    let fieldType = field?.type;
    if (!title) {
      const pairs = Object.entries(field);
      if (pairs.length === 1) {
        title = pairs[0][0];
        const def = pairs[0][1];
        if (def && typeof def === 'object' && !Array.isArray(def)) {
          fieldType = (def as any).type ?? (def as any).ref ?? fieldType;
        }
      }
    }
    if (!title || isScheduleMetaKey(String(title))) continue;
    if (data[title] !== undefined) continue;
    if (fieldType === 'boolean') data[title] = false;
    else if (fieldType === 'number' || fieldType === 'integer') data[title] = null;
    else if (fieldType === 'array' || fieldType === 'images' || fieldType === 'dataSet') data[title] = [];
    else data[title] = '';
  }
  const preferred = ['title', 'name', 'label', 'task', 'description'];
  if (extra.scheduleLabel) {
    const match = preferred.find((key) => Object.prototype.hasOwnProperty.call(data, key));
    if (match && (data[match] === '' || data[match] == null)) data[match] = extra.scheduleLabel;
  }
  return data;
}

export function buildVirtualScheduleItem(
  slot: ScheduleSlot,
  schema: any,
  datasetId?: string,
  missed = false,
  upcoming = false,
) {
  return {
    _id: `${VIRTUAL_SCHEDULE_PREFIX}${slot.scheduleKey}`,
    _virtual: true,
    _missed: missed,
    _upcoming: upcoming,
    dataId: datasetId,
    createdAt: slot.scheduledAt,
    data: emptyItemDataFromSchema(schema, {
      completed: false,
      scheduleKey: slot.scheduleKey,
      scheduledAt: slot.scheduledAt,
      schedulePreset: slot.schedulePreset,
      scheduleLabel: slot.scheduleLabel,
      _scheduled: true,
    }),
  };
}

export function getScheduleSlotTone(item: any): ScheduleSlotTone | null {
  if (!isScheduledDataItem(item)) return null;
  if (item?._missed) return 'missed';
  if (item?._upcoming) return 'upcoming';
  return 'scheduled';
}

export function getScheduleSlotBadgeKey(item: any): 'missedScheduleSlotBadge' | 'upcomingScheduleSlotBadge' | 'scheduledCardBadge' {
  const tone = getScheduleSlotTone(item);
  if (tone === 'missed') return 'missedScheduleSlotBadge';
  if (tone === 'upcoming') return 'upcomingScheduleSlotBadge';
  return 'scheduledCardBadge';
}

export function getScheduleSlotBadgeLabel(item: any, t: (key: string, options?: Record<string, unknown>) => string): string {
  const label = getItemScheduleLabel(item);
  const tone = getScheduleSlotTone(item);
  if (tone === 'missed') {
    return label ? t('missedScheduleSlotBadgeWithLabel', { label }) : t('missedScheduleSlotBadge');
  }
  if (tone === 'upcoming') {
    return label ? t('upcomingScheduleSlotBadgeWithLabel', { label }) : t('upcomingScheduleSlotBadge');
  }
  return label || t('scheduledCardBadge');
}

export function getScheduleSlotChipColors(item: any, theme: boolean) {
  const tone = getScheduleSlotTone(item);
  if (tone === 'missed') {
    return {
      backgroundColor: theme ? 'rgba(245,158,11,0.16)' : 'rgba(245,158,11,0.22)',
      color: theme ? '#9a3412' : '#fdba74',
    };
  }
  if (tone === 'upcoming') {
    return {
      backgroundColor: theme ? 'rgba(13,148,136,0.14)' : 'rgba(45,212,191,0.18)',
      color: theme ? '#0f766e' : '#5eead4',
    };
  }
  return {
    backgroundColor: theme ? 'rgba(14,165,233,0.12)' : 'rgba(56,189,248,0.18)',
    color: theme ? '#0369a1' : '#7dd3fc',
  };
}

export function mergeScheduleVirtualItems(
  realItems: any[],
  options: {
    completable?: boolean;
    settings?: RepeatableDatasetSettings;
    schema?: any;
    createdAt?: string | Date;
    datasetId?: string;
    missedCardLimit?: number;
    upcomingCardLimit?: number;
    filledKeys?: string[];
    now?: Date;
  },
) {
  const real = Array.isArray(realItems) ? realItems : [];
  const settings = options.settings || {};
  if (!options.completable || settings.repeatable !== true) {
    return {
      items: real,
      missed: [] as ScheduleSlot[],
      upcoming: [] as ScheduleSlot[],
      current: null as ScheduleSlot | null,
    };
  }
  const filled = [
    ...(options.filledKeys || []),
    ...real.map((item) => item?.data?.scheduleKey).filter(Boolean),
  ].map(String);
  const classified = classifyScheduleSlots(settings, filled, {
    from: options.createdAt,
    now: options.now || new Date(),
    upcomingLimit: options.upcomingCardLimit ?? VISIBLE_UPCOMING_SLOT_LIMIT,
  });
  const virtuals: any[] = [];
  for (const slot of classified.upcoming) {
    virtuals.push(buildVirtualScheduleItem(slot, options.schema, options.datasetId, false, true));
  }
  if (classified.current) {
    virtuals.push(buildVirtualScheduleItem(classified.current, options.schema, options.datasetId, false));
  }
  const missedLimit = options.missedCardLimit ?? VISIBLE_MISSED_SLOT_LIMIT;
  const visibleMissed = classified.missed.slice(-missedLimit);
  for (const slot of visibleMissed.slice().reverse()) {
    virtuals.push(buildVirtualScheduleItem(slot, options.schema, options.datasetId, true));
  }
  return {
    items: [...virtuals, ...real],
    missed: visibleMissed,
    upcoming: classified.upcoming,
    current: classified.current,
  };
}
